import { PROXY, dealProps } from "./constants";

// fetchDeals — paginates through all open pipeline deals.
// Passes the mapped property names so custom fields are always returned.
const fetchDeals = async (token, settings) => {
  const props = dealProps(settings);
  let all = [], after = undefined;
  while (true) {
    const url = `${PROXY}?endpoint=deals&properties=${encodeURIComponent(props)}${after ? `&after=${after}` : ""}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HubSpot API error: ${res.status}`);
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    all = [...all, ...(data.results || [])];
    if (data.paging?.next?.after) after = data.paging.next.after;
    else break;
  }
  return all;
};

const fetchWonDeals = async (token, settings) => {
  const mappings = settings?.stages?.mappings || [];
  const wonIds   = mappings.filter(m => m.type === "won").map(m => String(m.id));
  const fm       = settings?.fieldMap || {};
  const stageField = fm.dealstage || "dealstage";
  const amountField = fm.amount   || "amount";
  const closedateField = fm.closedate || "closedate";
  const ownerField = fm.owner    || "hubspot_owner_id";

  // Date filter: last 2 years to catch full-year bookings
  const fromTs = String(Date.now() - 2 * 365 * 24 * 60 * 60 * 1000);

  // Build filter: if we have mapped won stage IDs use them, otherwise fall back to closedwon
  const stageFilters = wonIds.length > 0
    ? wonIds.map(id => ({ propertyName: stageField, operator: "EQ", value: id }))
    : [{ propertyName: stageField, operator: "EQ", value: "closedwon" }];

  // HubSpot OR = multiple filterGroups; each group = AND
  const filterGroups = stageFilters.map(f => ({
    filters: [
      f,
      { propertyName: closedateField, operator: "GTE", value: fromTs },
    ]
  }));

  try {
    let all = [], after;
    for (let page = 0; page < 20; page++) {
      const body = { filterGroups, properties: ["dealname", amountField, stageField, closedateField, "createdate", ownerField, "dealtype", "hs_deal_stage_probability"], limit: 100, ...(after ? { after } : {}) };
      const res = await fetch(`${PROXY}?endpoint=deals_search`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) break;
      const data = await res.json();
      if (data.error) break;
      all = [...all, ...(data.results || [])];
      if (data.paging?.next?.after) { after = data.paging.next.after; await new Promise(r => setTimeout(r, 200)); }
      else break;
    }
    return all;
  } catch { return []; }
};

const fetchOwners = async (token) => {
  try {
    const res = await fetch(`${PROXY}?endpoint=owners`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return [];
    const data = await res.json();
    return (data.results || []).map(o => ({
      id: String(o.id),
      name: `${o.firstName || ""} ${o.lastName || ""}`.trim(),
      email: o.email || "",
      createdAt: o.createdAt || "",  // ISO string e.g. "2024-08-08T12:00:00.000Z"
    }));
  } catch { return []; }
};

const calcMetrics = (deals, wonDeals, settings) => {
  // Resolve mapped field names — fall back to HubSpot defaults if not configured
  const fm = settings?.fieldMap || {};
  const F = {
    amount:      fm.amount      || "amount",
    closedate:   fm.closedate   || "closedate",
    createdate:  fm.createdate  || "createdate",
    dealstage:   fm.dealstage   || "dealstage",
    owner:       fm.owner       || "hubspot_owner_id",
    dealtype:    fm.dealtype    || "dealtype",
    dealname:    fm.dealname    || "dealname",
    probability: fm.probability || "hs_deal_stage_probability",
  };

  // Use new mappings format if available, fall back to legacy list format
  const mappings = settings.stages?.mappings || [];
  const wonIds  = mappings.filter(m=>m.type==="won").map(m=>m.id.toLowerCase());
  const lostIds = mappings.filter(m=>m.type==="lost").map(m=>m.id.toLowerCase());
  const pipeIds = mappings.filter(m=>m.type==="pipeline").map(m=>m.id.toLowerCase());
  const hasMappings = mappings.length > 0;

  // Legacy fallback
  const closedWonStage  = hasMappings ? null : (settings.stages?.list||[]).find(s=>s.label==="Closed Won")?.id  || "closedwon";
  const closedLostStage = hasMappings ? null : (settings.stages?.list||[]).find(s=>s.label==="Closed Lost")?.id || "closedlost";

  const isWon  = (stage) => hasMappings ? wonIds.includes(stage.toLowerCase())  : stage.toLowerCase()===closedWonStage.toLowerCase()  || stage.toLowerCase()==="closedwon";
  const isLost = (stage) => hasMappings ? lostIds.includes(stage.toLowerCase()) : stage.toLowerCase()===closedLostStage.toLowerCase() || stage.toLowerCase()==="closedlost";
  const isPipeline = (stage) => hasMappings ? pipeIds.includes(stage.toLowerCase()) : (!isWon(stage) && !isLost(stage));
  
  const openDeals = deals.filter(d => {
    const stage = d.properties[F.dealstage]?.toLowerCase()||"";
    return isPipeline(stage);
  });
  
  const pipeline = openDeals.reduce((a,d)=>a+(+d.properties[F.amount]||0),0);
  const target = +(settings.revenue?.target)||0;
  const coverage = target>0 ? pipeline/target : 0;
  const gap = Math.max(0, target*3 - pipeline);

  const wonFromDeals = deals.filter(d=>{ const s=d.properties[F.dealstage]?.toLowerCase()||""; return isWon(s); });
  const won = hasMappings ? wonFromDeals.length : wonDeals.length;
  const wonDealsFinal = hasMappings ? wonFromDeals : wonDeals;

  // Filter won deals to current fiscal year for YTD calculations
  const fiscalYear = +settings.company?.fiscalYear || new Date().getFullYear();
  const fyStart   = new Date(`${fiscalYear}-01-01`).getTime();
  const l12mStart = Date.now() - 365 * 86400000; // rolling 12 months
  const fyEnd   = new Date(`${fiscalYear}-12-31`).getTime();
  const ytdDeals = wonDealsFinal.filter(d => {
    const closeDate = d.properties[F.closedate] ? new Date(d.properties[F.closedate]).getTime() : 0;
    return closeDate >= fyStart && closeDate <= fyEnd;
  });
  const lost = deals.filter(d=>{ const s=d.properties[F.dealstage]?.toLowerCase()||""; return isLost(s); }).length;
  // Filter lost to fiscal year for accurate YTD win rate
  const ytdLost = deals.filter(d => {
    const s = d.properties[F.dealstage]?.toLowerCase() || "";
    if (!isLost(s)) return false;
    const closeDate = d.properties[F.closedate] ? new Date(d.properties[F.closedate]).getTime() : 0;
    return closeDate >= fyStart && closeDate <= fyEnd;
  });
  // Win rate and avg deal size scoped to fiscal year — not all time
  const winRate     = (ytdDeals.length + ytdLost.length) > 0 ? ytdDeals.length / (ytdDeals.length + ytdLost.length) : 0;
  // Rolling 12 months win rate — more stable signal, always has a full sample
  const l12Deals    = wonDealsFinal.filter(d => { const t = d.properties[F.closedate] ? new Date(d.properties[F.closedate]).getTime() : 0; return t >= l12mStart; });
  const l12LostD    = deals.filter(d => { const s=(d.properties[F.dealstage]||"").toLowerCase(); if(!isLost(s)) return false; const t=d.properties[F.closedate]?new Date(d.properties[F.closedate]).getTime():0; return t>=l12mStart; });
  const winRateL12m = (l12Deals.length + l12LostD.length) > 0 ? l12Deals.length / (l12Deals.length + l12LostD.length) : winRate;
  const avgDealSize = ytdDeals.length > 0 ? ytdDeals.reduce((a,d)=>a+(+d.properties[F.amount]||0),0) / ytdDeals.length : 0;
  const ytdBookings = ytdDeals.reduce((a,d)=>a+(+d.properties[F.amount]||0),0);
  const allTimeWonBookings = wonDealsFinal.reduce((a,d)=>a+(+d.properties[F.amount]||0),0);

  // Per-rep metrics keyed by HubSpot owner ID
  const repMetrics = {};
  ytdDeals.forEach(d => {
    const oid = String(d.properties[F.owner] || "");
    if (!oid) return;
    repMetrics[oid] = repMetrics[oid] || { ytd: 0, pipeline: 0, wonCount: 0, lostCount: 0, openCount: 0 };
    repMetrics[oid].ytd += (+d.properties[F.amount] || 0);
    repMetrics[oid].wonCount++;
  });
  openDeals.forEach(d => {
    const oid = String(d.properties[F.owner] || "");
    if (!oid) return;
    repMetrics[oid] = repMetrics[oid] || { ytd: 0, pipeline: 0, wonCount: 0, lostCount: 0, openCount: 0 };
    repMetrics[oid].pipeline += (+d.properties[F.amount] || 0);
    repMetrics[oid].openCount++;
  });
  deals.filter(d=>{ const s=d.properties[F.dealstage]?.toLowerCase()||""; return isLost(s); }).forEach(d => {
    const oid = String(d.properties[F.owner] || "");
    if (!oid) return;
    repMetrics[oid] = repMetrics[oid] || { ytd: 0, pipeline: 0, wonCount: 0, lostCount: 0, openCount: 0 };
    repMetrics[oid].lostCount++;
  });

  // Segment metrics — attributed by property or by rep->segment mapping
  const segmentMetrics = {};
  const attrMode = settings.segments?.attribution?.mode || "rep";
  const attrProp = settings.segments?.attribution?.property || "";
  const attrValueMap = settings.segments?.attribution?.valueMap || {};

  if (attrMode === "property" && attrProp) {
    // Attribute by deal property value → segment name
    deals.forEach(d => {
      const propVal = d.properties?.[attrProp] || "";
      const seg = attrValueMap[propVal];
      if (!seg) return;
      const stage = d.properties[F.dealstage]?.toLowerCase() || "";
      segmentMetrics[seg] = segmentMetrics[seg] || { pipeline: 0, ytd: 0, wonCount: 0, lostCount: 0, openCount: 0 };
      if (isPipeline(stage)) { segmentMetrics[seg].pipeline += (+d.properties[F.amount]||0); segmentMetrics[seg].openCount++; }
      else if (isWon(stage))  { segmentMetrics[seg].ytd += (+d.properties[F.amount]||0); segmentMetrics[seg].wonCount++; }
      else if (isLost(stage)) { segmentMetrics[seg].lostCount++; }
    });
  } else if (settings.team?.reps) {
    // Default: attribute by rep owner → segment
    (settings.team?.reps||[]).forEach(rep => {
      if (!rep.hubspotOwnerId || !rep.segment) return;
      const rd = repMetrics[rep.hubspotOwnerId];
      if (!rd) return;
      const seg = rep.segment;
      segmentMetrics[seg] = segmentMetrics[seg] || { pipeline: 0, ytd: 0, wonCount: 0, lostCount: 0, openCount: 0 };
      segmentMetrics[seg].pipeline += rd.pipeline;
      segmentMetrics[seg].ytd += rd.ytd;
      segmentMetrics[seg].wonCount += rd.wonCount;
      segmentMetrics[seg].lostCount += rd.lostCount;
      segmentMetrics[seg].openCount += rd.openCount;
    });
  }

  // Stage breakdown
  const stageMap = {};
  openDeals.forEach(d=>{
    const s = d.properties[F.dealstage]||"Unknown";
    stageMap[s] = (stageMap[s]||0) + (+d.properties[F.amount]||0);
  });

  const now = Date.now();
  const msDay = 86400000;

  // ── Stale close dates (overdue open deals) ──
  const stale = openDeals.filter(d => d.properties[F.closedate] && new Date(d.properties[F.closedate]).getTime() < now).length;
  const staleRate = openDeals.length > 0 ? stale / openDeals.length : 0;

  // ── Field completeness ──
  const withAmount    = openDeals.filter(d => +d.properties[F.amount] > 0).length;
  const withCloseDate = openDeals.filter(d => !!d.properties[F.closedate]).length;
  const withOwner     = openDeals.filter(d => !!d.properties[F.owner]).length;
  const withDealType  = openDeals.filter(d => !!(d.properties?.dealtype || d.properties?.[F.dealtype])).length;
  const totalOpen     = openDeals.length || 1;

  const fieldUsagePct = (withAmount + withCloseDate) / (totalOpen * 2);
  const completePct   = openDeals.filter(d => +d.properties[F.amount] > 0 && d.properties[F.closedate] && d.properties[F.owner]).length / totalOpen;
  const ownerPct      = withOwner / totalOpen;
  const dealTypePct   = withDealType / totalOpen;

  // ── Overdue severity: close date > 30 days past ──
  const severelyStale = openDeals.filter(d => {
    if (!d.properties[F.closedate]) return false;
    return (now - new Date(d.properties[F.closedate]).getTime()) > 30 * msDay;
  }).length;
  const severeStaleRate = severelyStale / totalOpen;

  // ── Deals older than 12 months (zombie pipeline) ──
  const zombie = openDeals.filter(d => {
    if (!d.properties[F.createdate]) return false;
    return (now - new Date(d.properties[F.createdate]).getTime()) > 365 * msDay;
  });
  const zombieRate = zombie.length / totalOpen;

  // ── No activity in 30+ days on open deals ──
  const cut30 = now - 30 * msDay;
  const darkDeals = openDeals.filter(d => {
    // Only count genuine logged activity — NOT hs_lastmodifieddate which fires on any edit
    const lastAct = d.properties?.notes_last_activity_date;
    if (!lastAct) return true; // no activity ever logged = dark
    return new Date(lastAct).getTime() < cut30;
  });
  const darkDealRate = darkDeals.length / totalOpen;

  // ── Single-threaded open deals (1 or 0 contacts) ──
  const singleThread = openDeals.filter(d => +(d.properties?.num_associated_contacts || 0) <= 1).length;
  const singleThreadRate = singleThread / totalOpen;

  // ── Early stage concentration ──
  const earlyStageIds = (settings.stages?.list || []).filter(s => ["Prospecting","Discovery"].includes(s.label)).map(s => s.id?.toLowerCase());
  const earlyCount = openDeals.filter(d => earlyStageIds.includes(d.properties[F.dealstage]?.toLowerCase()) || ["prospecting","appointmentscheduled"].includes(d.properties[F.dealstage]?.toLowerCase())).length;
  const earlyConc = earlyCount / totalOpen;

  // ── Data Integrity: sandbagging + ghost wins ──
  const msDay3 = msDay * 3;
  const sandbagged = wonDealsFinal.filter(d => {
    const cd = d.properties[F.closedate]; const cr = d.properties[F.createdate];
    if (!cd || !cr) return false;
    return (new Date(cd).getTime() - new Date(cr).getTime()) < msDay3;
  });
  const sandbaggingRate = wonDealsFinal.length > 0 ? sandbagged.length / wonDealsFinal.length : 0;
  const ghostWins = wonDealsFinal.filter(d => +(d.properties?.num_associated_contacts || 0) === 0);
  const ghostWinRate = wonDealsFinal.length > 0 ? ghostWins.length / wonDealsFinal.length : 0;
  const dataIntegrityScore = Math.round(
    (sandbaggingRate < 0.05 ? 5 : sandbaggingRate < 0.1 ? 4 : sandbaggingRate < 0.2 ? 3 : sandbaggingRate < 0.35 ? 2 : 1) * 0.5 +
    (ghostWinRate < 0.05 ? 5 : ghostWinRate < 0.15 ? 4 : ghostWinRate < 0.3 ? 3 : ghostWinRate < 0.5 ? 2 : 1) * 0.5
  );

  // ── Deal Completeness: contacts + next step + deal type ──
  const withContacts = openDeals.filter(d => +(d.properties?.num_associated_contacts || 0) > 0).length;
  const withNextStep = openDeals.filter(d => !!(d.properties?.hs_next_step)).length;
  const contactsPct  = totalOpen > 0 ? withContacts / totalOpen : 0;
  const nextStepPct  = totalOpen > 0 ? withNextStep / totalOpen : 0;
  const dealCompletenessScore = Math.round(
    (contactsPct > 0.9 ? 5 : contactsPct > 0.75 ? 4 : contactsPct > 0.55 ? 3 : contactsPct > 0.35 ? 2 : 1) * 0.4 +
    (nextStepPct > 0.8 ? 5 : nextStepPct > 0.6 ? 4 : nextStepPct > 0.4 ? 3 : nextStepPct > 0.2 ? 2 : 1) * 0.4 +
    (dealTypePct  > 0.9 ? 5 : dealTypePct  > 0.7 ? 4 : dealTypePct  > 0.5 ? 3 : dealTypePct  > 0.3 ? 2 : 1) * 0.2
  );

  // ── Stage Velocity: stuck + zombie deals ──
  const avgCycleMs = (() => {
    const closed = wonDealsFinal.filter(d => d.properties[F.createdate] && d.properties[F.closedate]);
    if (!closed.length) return 60 * msDay;
    const valid = closed.map(d => new Date(d.properties[F.closedate]).getTime() - new Date(d.properties[F.createdate]).getTime()).filter(ms => ms > 0);
    return valid.length ? valid.reduce((a,v) => a+v, 0) / valid.length : 60 * msDay;
  })();
  const stuckDeals = openDeals.filter(d => {
    if (!d.properties[F.createdate]) return false;
    return (now - new Date(d.properties[F.createdate]).getTime()) > avgCycleMs * 1.5;
  });
  const stuckRate = totalOpen > 0 ? stuckDeals.length / totalOpen : 0;
  const velocityPenalty = zombieRate > 0.1 ? 1 : zombieRate > 0.05 ? 0.5 : 0;
  const stageVelocityScore = Math.max(1, Math.round(
    (stuckRate < 0.1 ? 5 : stuckRate < 0.2 ? 4 : stuckRate < 0.35 ? 3 : stuckRate < 0.5 ? 2 : 1) - velocityPenalty
  ));

  // ── Activity Tracking: dark deals + owner coverage ──
  const activityScore = Math.round(
    (darkDealRate < 0.1 ? 5 : darkDealRate < 0.25 ? 4 : darkDealRate < 0.4 ? 3 : darkDealRate < 0.6 ? 2 : 1) * 0.6 +
    (ownerPct > 0.95 ? 5 : ownerPct > 0.8 ? 4 : ownerPct > 0.6 ? 3 : ownerPct > 0.4 ? 2 : 1) * 0.4
  );

  // ── Forecast Reliability: stale dates + severely overdue ──
  const forecastScore = Math.round(
    (staleRate < 0.1 ? 5 : staleRate < 0.2 ? 4 : staleRate < 0.35 ? 3 : staleRate < 0.5 ? 2 : 1) * 0.5 +
    (severeStaleRate < 0.05 ? 5 : severeStaleRate < 0.15 ? 4 : severeStaleRate < 0.3 ? 3 : severeStaleRate < 0.5 ? 2 : 1) * 0.5
  );

  // ── Pipeline Quality: single-threaded + zombie ──
  const pipelineQualityScore = Math.round(
    (singleThreadRate < 0.2 ? 5 : singleThreadRate < 0.35 ? 4 : singleThreadRate < 0.5 ? 3 : singleThreadRate < 0.65 ? 2 : 1) * 0.6 +
    (zombieRate < 0.02 ? 5 : zombieRate < 0.05 ? 4 : zombieRate < 0.1 ? 3 : zombieRate < 0.2 ? 2 : 1) * 0.4
  );

  const crmAreas = {
    pipelineStages:      Math.round(earlyConc > 0.7 ? 2 : earlyConc > 0.5 ? 3 : earlyConc > 0.35 ? 4 : 5),
    fieldUsage:          Math.round(fieldUsagePct > 0.9 ? 5 : fieldUsagePct > 0.75 ? 4 : fieldUsagePct > 0.55 ? 3 : fieldUsagePct > 0.35 ? 2 : 1),
    dataCompleteness:    Math.round(completePct > 0.85 ? 5 : completePct > 0.65 ? 4 : completePct > 0.45 ? 3 : completePct > 0.25 ? 2 : 1),
    forecastReliability: forecastScore,
    activityTracking:    activityScore,
    pipelineQuality:     pipelineQualityScore,
    dataIntegrity:       dataIntegrityScore,
    dealCompleteness:    dealCompletenessScore,
    stageVelocity:       stageVelocityScore,
  };

  const WEIGHTS = {
    pipelineStages:      0.08,
    fieldUsage:          0.10,
    dataCompleteness:    0.14,
    forecastReliability: 0.16,
    activityTracking:    0.14,
    pipelineQuality:     0.10,
    dataIntegrity:       0.12,
    dealCompleteness:    0.12,
    stageVelocity:       0.04,
  };
  const crmScore = +(Object.entries(crmAreas).reduce((a, [k, v]) => a + v * (WEIGHTS[k] || 0.1), 0) * 2).toFixed(1);


  return { pipeline, coverage, gap, winRate, winRateL12m, avgDealSize, openDeals, stageMap, stale, staleRate, won, lost, ytdBookings, allTimeWonBookings, totalDeals: deals.length, repMetrics, segmentMetrics, crmScore, crmAreas, fieldUsagePct, completePct, ownerPct, wonDealsFinal, lostDeals: deals.filter(d=>isLost(d.properties[F.dealstage]?.toLowerCase()||'')), sandbagged, ghostWins, stuckDeals, contactsPct, nextStepPct, sandbaggingRate, ghostWinRate, stuckRate, zombie, zombieRate, darkDeals, darkDealRate, severelyStale, severeStaleRate, singleThreadRate, dealTypePct };
};

const fetchPipelineStages = async (token) => {
  try {
    const res = await fetch(`${PROXY}?endpoint=pipelines`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return [];
    const data = await res.json();
    const stages = [];
    (data.results || []).forEach(pipeline => {
      (pipeline.stages || []).forEach(stage => {
        stages.push({
          id:         stage.id,
          label:      stage.label,
          pipeline:   pipeline.label,
          probability: stage.metadata?.probability || "0",
        });
      });
    });
    return stages;
  } catch { return []; }
};


export { fetchDeals, fetchWonDeals, fetchOwners, calcMetrics, fetchPipelineStages };
